/**
 * Login Page ViewModel
 *
 * Handles all logic and state for the LoginPage component.
 * In normal browsers: Uses Web3Auth for social authentication (Google, Facebook).
 * In webviews: Uses session bridge flow (opens device browser for login).
 * Following MVVM pattern: returns { state, actions } object.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isEmbedded } from '../services/environment';
import { authBridgeApi } from '../services/auth-bridge.api';
import { useAuthStore } from '../stores/useAuthStore';
import { useAuthNavigation } from '../hooks/useAuthNavigation';
import { authApi } from '../services/auth.api';
import { startTokenRefreshScheduler } from '@/lib/token-refresh';
import type { AuthUser } from '../stores/useAuthStore';

const BRIDGE_POLL_INTERVAL = 2000; // 2 seconds
const BRIDGE_TIMEOUT = 300000; // 5 minutes

interface LoginPageState {
  isLoading: boolean;
  error: string | null;
  isBridgePolling: boolean;
  isEmbeddedContext: boolean;
}

interface LoginPageActions {
  handleFacebookLogin: () => void;
  handleGoogleLogin: () => void;
  cancelBridgePolling: () => void;
}

export function useLoginPageViewModel(): { state: LoginPageState; actions: LoginPageActions } {
  const {
    isLoading: authLoading,
    error: authError,
    loginWithGoogle,
    loginWithFacebook
  } = useAuth();
  const { setTokens, setUser, setLoading } = useAuthStore();
  const { navigateToHome } = useAuthNavigation();

  const [isBridgePolling, setIsBridgePolling] = useState(false);
  const [bridgeError, setBridgeError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsBridgePolling(false);
  }, []);

  const startBridgeFlow = useCallback(
    async (provider: 'google' | 'facebook') => {
      setBridgeError(null);
      setIsBridgePolling(true);

      try {
        // 1. Create bridge on backend
        const bridgeId = await authBridgeApi.createBridge();

        // 2. Open device browser with bridge login page
        const baseUrl = window.location.origin;
        const bridgeUrl = `${baseUrl}/auth/bridge-login?bridgeId=${bridgeId}&provider=${provider}`;
        const newWindow = window.open(bridgeUrl, '_blank');

        // If popup was blocked, try navigating directly (fallback for strict webviews)
        if (!newWindow) {
          window.location.href = bridgeUrl;
          return;
        }

        // 3. Set timeout for 5 minutes
        timeoutRef.current = setTimeout(() => {
          stopPolling();
          setBridgeError('Login expired. Please try again.');
        }, BRIDGE_TIMEOUT);

        // 4. Poll for bridge status — use recursive setTimeout to prevent
        // overlapping requests on slow networks (setInterval doesn't wait)
        const pollOnce = async () => {
          try {
            const result = await authBridgeApi.getBridgeStatus(bridgeId);

            if (result.status === 'completed' && result.accessToken && result.refreshToken) {
              stopPolling();
              setLoading(true);

              // Store tokens
              setTokens(result.accessToken, result.refreshToken);

              // Start token refresh scheduler
              startTokenRefreshScheduler();

              // Fetch user profile
              try {
                const profile = await authApi.getProfile();
                const authUser: AuthUser = {
                  id: profile.id,
                  email: profile.email,
                  pseudo: profile.pseudo,
                  points: profile.points,
                  country: profile.country,
                  ...(result.user as Partial<AuthUser>)
                };
                setUser(authUser);
                navigateToHome();
              } catch {
                // Profile fetch failed but we have tokens — still navigate
                setUser({ ...(result.user as Partial<AuthUser>) } as AuthUser);
                navigateToHome();
              }

              setLoading(false);
              return; // Done — don't schedule next poll
            } else if (result.status === 'expired') {
              stopPolling();
              setBridgeError('Login expired. Please try again.');
              return;
            }
          } catch {
            // Polling error — will retry on next cycle
            console.warn('Bridge polling error, will retry...');
          }

          // Schedule next poll only after current one finishes
          pollingRef.current = setTimeout(() => void pollOnce(), BRIDGE_POLL_INTERVAL);
        };

        pollingRef.current = setTimeout(() => void pollOnce(), BRIDGE_POLL_INTERVAL);
      } catch (err) {
        stopPolling();
        const message = err instanceof Error ? err.message : 'Failed to start login';
        setBridgeError(message);
      }
    },
    [stopPolling, setTokens, setUser, setLoading, navigateToHome]
  );

  const handleGoogleLogin = useCallback(() => {
    if (isEmbedded) {
      void startBridgeFlow('google');
    } else {
      void loginWithGoogle();
    }
  }, [startBridgeFlow, loginWithGoogle]);

  const handleFacebookLogin = useCallback(() => {
    if (isEmbedded) {
      void startBridgeFlow('facebook');
    } else {
      void loginWithFacebook();
    }
  }, [startBridgeFlow, loginWithFacebook]);

  const cancelBridgePolling = useCallback(() => {
    stopPolling();
    setBridgeError(null);
  }, [stopPolling]);

  const state: LoginPageState = {
    isLoading: authLoading || isBridgePolling,
    error: bridgeError ?? authError,
    isBridgePolling,
    isEmbeddedContext: isEmbedded
  };

  const actions: LoginPageActions = {
    handleFacebookLogin,
    handleGoogleLogin,
    cancelBridgePolling
  };

  return { state, actions };
}
